import type { useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { EditorContent, useEditor } from "@tiptap/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  cn,
} from "@ndd/ui";
import { useState, type ReactElement } from "react";
import type {
  PatientQuickNoteConflictResponse,
  PatientQuickNoteResponse,
  PatientQuickNoteRevisionResponse,
  PatientQuickNoteSaveRequest,
} from "../../../../shared/api-types";
import {
  richTextEditorExtensions,
  type RichTextDocument,
} from "../../../../shared/rich-text-document";
import { NOTE_PROSE_CLASSES, RichTextEditor } from "@/components/v2/rich-text-editor";
import { useFormDirtyGuard } from "@/components/v2/use-form-dirty-guard";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import type { AppointmentNoteSaveOutcome } from "@/lib/appointment-note-autosave";
import {
  quickNotePath,
  quickNoteQueryKey,
  quickNoteSubjectId,
  type QuickNoteSubject,
} from "@/lib/patient-quick-note-subject";
import { EditorConflictBanner, EditorSaveStatus } from "./editor-status";
import { PatientQuickNoteHealthieSync } from "./patient-quick-note-healthie-sync";
import {
  PatientQuickNoteInHealthieSection,
  PatientQuickNoteSourceLabel,
} from "./patient-quick-note-in-healthie";
import {
  classifyAutosaveFailure,
  useRevisionedNoteAutosave,
} from "./use-appointment-note-autosave";
import { CARD_TITLE } from "./workspace-look";

type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

/**
 * The Patient Quick Note (NO-445): one note per Patient, shown on every
 * Appointment the RD owns for them. It mirrors the Appointment Note editor —
 * autosave, the save-state line, and the conflict banner that keeps the RD's
 * text. Its own query, so a failed load never blocks the Appointment Note.
 */
export function PatientQuickNote({
  appointmentId,
  getToken,
}: {
  appointmentId: string;
  getToken: ClerkGetToken;
}): ReactElement {
  return (
    <section aria-label="Patient quick note" className="mt-[22px] shrink-0">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h2
          className={cn(
            "font-sans text-body-lg font-semibold text-foreground",
            CARD_TITLE
          )}
        >
          Patient quick note
        </h2>
        <p className="text-xs text-muted-foreground">
          Shown on all of this patient&apos;s appointments
        </p>
      </div>
      <PatientQuickNoteBody subject={{ appointmentId }} getToken={getToken} />
    </section>
  );
}

/**
 * The note's own query and its editor, or a loading or failed line. The
 * patient page (NO-849) opens it against the Client and passes `onDone`,
 * which adds a Done control that closes the editor once the latest edit is
 * saved, or after the RD chooses to leave without saving.
 */
export function PatientQuickNoteBody({
  subject,
  getToken,
  onDone,
}: {
  subject: QuickNoteSubject;
  getToken: ClerkGetToken;
  onDone?: () => void;
}): ReactElement {
  const note = useQuery({
    queryKey: quickNoteQueryKey(subject),
    queryFn: () =>
      authenticatedApi<{ data: PatientQuickNoteResponse }>(
        getToken,
        quickNotePath(subject)
      ),
    staleTime: 0,
    gcTime: 0,
  });

  if (!note.data) {
    return (
      <PatientQuickNotePlaceholder
        failed={note.isError}
        onRetry={() => void note.refetch()}
      />
    );
  }

  return (
    <PatientQuickNoteEditor
      key={quickNoteSubjectId(subject)}
      subject={subject}
      initial={note.data.data}
      getToken={getToken}
      onDone={onDone}
    />
  );
}

/**
 * The note as text, for the patient page's About them card (NO-849), in the
 * read-only shape of `AppointmentNoteReadOnly`. It reads `document` once, so
 * the caller keys it by Revision.
 */
export function PatientQuickNoteReadOnly({
  document,
}: {
  document: RichTextDocument;
}): ReactElement {
  const editor = useEditor({
    extensions: richTextEditorExtensions,
    content: document,
    editable: false,
    immediatelyRender: false,
  });

  return (
    <EditorContent
      editor={editor}
      className={cn(NOTE_PROSE_CLASSES, "bg-transparent px-0 py-0 text-foreground")}
    />
  );
}

function PatientQuickNotePlaceholder({
  failed,
  onRetry,
}: {
  failed: boolean;
  onRetry: () => void;
}): ReactElement {
  return (
    <div className="flex min-h-[72px] items-center justify-between gap-3 rounded-[10px] border border-border bg-background px-4 text-sm text-muted-foreground">
      {failed ? (
        <>
          <span role="status">Couldn&apos;t load the patient quick note.</span>
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </>
      ) : (
        <span role="status">Loading patient quick note…</span>
      )}
    </div>
  );
}

function PatientQuickNoteEditor({
  subject,
  initial,
  getToken,
  onDone,
}: {
  subject: QuickNoteSubject;
  initial: PatientQuickNoteResponse;
  getToken: ClerkGetToken;
  onDone?: () => void;
}): ReactElement {
  const { autosave, savedRevisionId, currentRevision } = useQuickNoteAutosave(
    subject,
    initial,
    getToken
  );
  const dirtyGuard = useFormDirtyGuard(autosave.snapshot.dirty);

  return (
    <>
      {/* A fixed height that scrolls inside, so a long note never pushes the
          Appointment Note and its footer out of view. */}
      <div className="flex h-[220px] min-h-0 flex-col">
        <RichTextEditor
          initialValue={initial.document}
          replacement={autosave.replacement}
          shouldFillAvailableHeight
          onChange={autosave.edit}
          toolbarEnd={
            <EditorSaveStatus
              status={autosave.snapshot.status}
              onRetry={autosave.retry}
            />
          }
          alert={
            autosave.snapshot.status === "conflict" ? (
              <EditorConflictBanner
                subject="note"
                onUseLatest={autosave.useLatest}
                onSaveMine={autosave.saveMine}
              />
            ) : undefined
          }
        />
      </div>

      <PatientQuickNoteSourceLabel revision={currentRevision} />

      <PatientQuickNoteHealthieSync
        subject={subject}
        savedRevisionId={savedRevisionId}
        getToken={getToken}
      />

      <PatientQuickNoteInHealthieSection inHealthie={initial.inHealthie} />

      {onDone ? (
        <QuickNoteDoneButton onDone={() => dirtyGuard.requestClose(onDone)} />
      ) : null}

      <UnsavedQuickNoteDialog dirtyGuard={dirtyGuard} />
    </>
  );
}

/** Closes the editor; the dirty guard holds it until the latest edit saves. */
function QuickNoteDoneButton({ onDone }: { onDone: () => void }): ReactElement {
  return (
    <Button
      type="button"
      variant="quiet-link"
      className="mt-3 h-auto px-0 py-0 text-body"
      onClick={onDone}
    >
      Done
    </Button>
  );
}

function useQuickNoteAutosave(
  subject: QuickNoteSubject,
  initial: PatientQuickNoteResponse,
  getToken: ClerkGetToken
) {
  // NO-750: each new saved Revision refreshes the Healthie sync line.
  const [savedRevisionId, setSavedRevisionId] = useState(initial.revision?.id ?? null);
  // NO-784: the server's current Revision, from the last load, save or
  // conflict, so the "Edited in Healthie" label always tracks it.
  const [currentRevision, setCurrentRevision] =
    useState<PatientQuickNoteRevisionResponse | null>(initial.revision);
  const autosave = useRevisionedNoteAutosave({
    initial,
    save: async (request) => {
      const outcome = await savePatientQuickNote(getToken, subject, request);

      if (outcome.status === "saved") {
        setSavedRevisionId(outcome.value.revision?.id ?? null);
        setCurrentRevision(outcome.value.revision);
      }

      if (outcome.status === "conflict") setCurrentRevision(outcome.current.revision);

      return outcome;
    },
    onError: captureAutosaveFailure,
  });

  return { autosave, savedRevisionId, currentRevision };
}

function UnsavedQuickNoteDialog({
  dirtyGuard,
}: {
  dirtyGuard: ReturnType<typeof useFormDirtyGuard>;
}): ReactElement {
  return (
    <AlertDialog
      open={dirtyGuard.showDiscardDialog}
      onOpenChange={dirtyGuard.handleDiscardDialogOpenChange}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Leave before this note is saved?</AlertDialogTitle>
          <AlertDialogDescription>
            The latest changes to the patient quick note could be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep editing</AlertDialogCancel>
          <AlertDialogAction onClick={() => dirtyGuard.confirmDiscard(() => {})}>
            Leave without saving
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

async function savePatientQuickNote(
  getToken: ClerkGetToken,
  subject: QuickNoteSubject,
  request: PatientQuickNoteSaveRequest
): Promise<AppointmentNoteSaveOutcome> {
  try {
    const response = await authenticatedApi<{ data: PatientQuickNoteResponse }>(
      getToken,
      quickNotePath(subject),
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      }
    );

    return { status: "saved", value: response.data };
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 409) {
      const body = error.body as PatientQuickNoteConflictResponse;

      if (body?.code === "patient_quick_note_conflict") {
        return { status: "conflict", current: body.current };
      }
    }

    throw error;
  }
}

function captureAutosaveFailure(error: unknown): void {
  Sentry.captureException(new Error("patient_quick_note_autosave_failed"), {
    tags: {
      ...classifyAutosaveFailure(error),
      operation: "save",
      source: "client",
      surface: "patient_quick_notes",
    },
  });
}
